import {
  CcfBox,
  CcfCalendarPlusIcon,
  CcfDeboucedInput,
  CcfListViewIcon,
  CcfTableViewIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from './ccf-consumer-card-interactions-tab-header.module.scss';
import { IconButton, InputAdornment } from '@mui/material';
import { FilterList, Search } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAppSpaceRatio } from '../../../../global.app.slice';
import CcfInteractionsFilterPopover from '../ccf-interactions-filter-popover/ccf-interactions-filter-popover';

/* eslint-disable-next-line */
export interface CcfConsumerCardInteractionsTabHeaderProps {
  interactionViewSelectHandler: (view: string) => void;
  selectedInteractionView: string;
  searchHandler: (searchQuery: string) => void;
  handleFilter: any;
}

const useStyles = makeStyles({
  searchInput: {
    '& .MuiOutlinedInput-root': {
      height: '35px', // Set your desired height here
    },
  },
});

/**
 * CcfConsumerCardOverviewTab - returns Interactions Tab Header in consumer card Interactions Tab
 * @param props - CcfConsumerCardInteractionsTabHeaderProps
 * @example - `<CcfConsumerCardInteractionsTabHeader />`
 */
export function CcfConsumerCardInteractionsTabHeader(
  props: CcfConsumerCardInteractionsTabHeaderProps
) {
  const classes = useStyles();
  const { interactionViewSelectHandler, searchHandler, handleFilter } = props;
  const [interactionsMinView, setInteractionsMinView] = useState(false);
  const appSpaceRatio = useSelector(getAppSpaceRatio);
  const [selectedInteractionView, setSelectedInteractionView] = useState(
    props.selectedInteractionView
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [translate] = useTranslator();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedFilterOption, setSelectedFilterOption] = useState('All');

  useEffect(() => {
    const isMinView = appSpaceRatio && appSpaceRatio <= 40;
    setInteractionsMinView(isMinView);
    if (isMinView) {
      handleInteractionView('List');
    }
  }, [appSpaceRatio]);

  /**
   * Function to select interaction view
   * @example handleInteractionView()
   */
  const handleInteractionView = useCallback(
    (selectedView: string) => {
      setSelectedInteractionView(selectedView);
      interactionViewSelectHandler(selectedView);
    },
    [interactionViewSelectHandler]
  );

  /**
   * Function to select interaction view
   * @example handleSearch()
   */
  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const query = event.target.value.toLowerCase();
      setSearchQuery(query);
      searchHandler(query);
    },
    [searchHandler]
  );

  useEffect(() => {
    const mockEvent = {
      target: { value: searchQuery },
    } as React.ChangeEvent<HTMLInputElement>;
    handleSearch(mockEvent); //call handleSearch to restore data without seacrh on component rereender
  }, []);

  const containerStyle = useMemo(
    () => ({
      width: interactionsMinView ? '50%' : '35%',
    }),
    [interactionsMinView]
  );

  /**
   * Function to open the filter popover
   * @example handlePopoverOpen()
   */
  const handlePopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Function to close the filter popover
   * @example handlePopoverClose()
   */
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <CcfBox className={styles.headercontainer}>
        <CcfBox style={containerStyle}>
          <CcfDeboucedInput
            size="small"
            style={{
              background: 'white',
            }}
            value={searchQuery}
            onChange={handleSearch}
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search htmlColor="grey" />
                </InputAdornment>
              ),
            }}
            placeholder={translate('search') + '...'}
            variant="outlined"
            fullWidth
            delay={1500}
          />
        </CcfBox>
        <CcfBox className={styles.btnContainer}>
          <IconButton style={{ pointerEvents: 'none' }}>
            <FilterList className={styles.btnIcon} />{' '}
          </IconButton>
          <IconButton>
            <CcfCalendarPlusIcon
              onClick={(e: any) => handlePopoverOpen(e)}
              className={styles.btnIcon}
            />{' '}
          </IconButton>
          {!interactionsMinView &&
            (selectedInteractionView === 'List' ? (
              <IconButton className="pointerEvent">
                <CcfTableViewIcon
                  className={styles.tableViewIcon}
                  onClick={() => handleInteractionView('Table')}
                />
              </IconButton>
            ) : (
              <IconButton className="pointerEvent">
                <CcfListViewIcon
                  className={styles.listViewIcon}
                  onClick={() => handleInteractionView('List')}
                />
              </IconButton>
            ))}
        </CcfBox>
      </CcfBox>
      <CcfInteractionsFilterPopover
        anchorEl={anchorEl}
        handlePopoverClose={handlePopoverClose}
        handleFilter={handleFilter}
        selectedFilterOption={selectedFilterOption}
      />
    </>
  );
}

export default CcfConsumerCardInteractionsTabHeader;
